"use client";

import React from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const narrativeBlocks = [{
  type: "text",
  content: "Moza Casa was founded on a simple observation: technology should serve the home, not dominate it. We began by stripping away the complexity of traditional smart home setups, focusing exclusively on the seamless elegance of Apple HomeKit. Our mission is to transform living spaces into intuitive sanctuaries where automation feels like second nature."
}, {
  type: "quote",
  content: "\"True intelligence in a home isn't about how many gadgets you have, but how little you notice them.\""
}, {
  type: "text",
  content: "We curate tips and tricks that prioritize 'Simple Living.' Every automation recipe we share is tested for reliability and aesthetic harmony. We believe that a smart home should be as beautiful as it is functional, blending high-tech precision with the warmth of a well-designed interior."
}, {
  type: "quote",
  content: "Digital Zen is the balance between technical excellence and the quiet comfort of home."
}, {
  type: "text",
  content: "By focusing on the Apple ecosystem, we ensure a level of privacy and integration that is unmatched. Our approach is editorial and intentional—we don't just show you how to connect a lightbulb; we show you how to craft an atmosphere. This is the future of the home, refined for the modern minimalist."
}];
function NarrativeBlock({
  block,
  index
}: {
  block: any;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.5,
    once: false
  });
  return <div data-section-id="223949" data-index={index} ref={ref} className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {block.type === "quote" ? <blockquote className="my-12 border-l-4 border-secondary pl-6 py-2">
          <p className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-primary leading-tight tracking-tight">
            "{block.content}"
          </p>
        </blockquote> : <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-serif">
          {block.content}
        </p>}
    </div>;
}
export default function KitchenOrigins() {
  return <section id="our-story" className="bg-background text-foreground py-16 md:py-24 px-4 md:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start lg:h-[800px]">
          
          {}
          <div className="relative h-[500px] lg:h-full w-full rounded-2xl bg-accent/30 border border-border/50 p-6 md:p-10">
            {}
            <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-background to-transparent z-10 pointer-events-none rounded-t-2xl" />
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent z-10 pointer-events-none rounded-b-2xl" />
            
            <ScrollArea className="h-full pr-6">
              <div className="space-y-12 py-12">
                <div className="mb-16">
                  <h2 className="text-sm font-bold tracking-widest text-secondary-foreground uppercase mb-4 font-default">
                    The Moza Method
                  </h2>
                  <h3 className="text-4xl md:text-6xl font-extrabold font-default tracking-tighter text-primary">
                    The Art of Simple Living
                  </h3>
                </div>

                {narrativeBlocks.map((block, i) => <NarrativeBlock key={i} block={block} index={i} />)}
                
                {}
                <div className="h-32" />
              </div>
            </ScrollArea>
          </div>

          {}
          <div className="relative h-[600px] lg:h-full w-full hidden md:block">
            <div className="absolute inset-0 w-full h-full">
              {}
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/minimalist_living_room_bw.png" alt="Minimalist living room with natural light" className="absolute top-0 left-0 w-[65%] h-[55%] object-cover rounded-2xl grayscale opacity-40 shadow-sm transition-transform duration-1000 hover:scale-105" />
              
              {}
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/homekit_interface_detail_bw.png" alt="Subtle smart home interface detail" className="absolute bottom-0 right-0 w-[70%] h-[60%] object-cover rounded-2xl grayscale opacity-30 shadow-sm transition-transform duration-1000 hover:scale-105" />
              
              {}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60%] h-[50%] z-20">
                <div className="w-full h-full relative rounded-2xl overflow-hidden shadow-xl border-8 border-background ring-1 ring-border">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/moza_casa_lifestyle_color.png" alt="Warmly lit modern home interior" className="w-full h-full object-cover transition-transform duration-700 hover:scale-110" />
                  {}
                  <div className="absolute inset-0 shadow-[inset_0_0_50px_rgba(230,213,195,0.2)] pointer-events-none mix-blend-overlay" />
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="md:hidden flex flex-col gap-6 mt-8">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/moza_casa_lifestyle_color.png" alt="Warmly lit modern home interior" className="w-full h-64 object-cover rounded-2xl shadow-md border-4 border-background" />
            <div className="grid grid-cols-2 gap-4">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/minimalist_living_room_bw.png" alt="Minimalist living room" className="w-full h-40 object-cover rounded-xl grayscale opacity-70" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647152/img/homekit_interface_detail_bw.png" alt="Smart home detail" className="w-full h-40 object-cover rounded-xl grayscale opacity-70" />
            </div>
          </div>

        </div>
      </div>
    </section>;
}